/**
 * Arc maths for the dial gauges, kept apart from React so the geometry can be
 * asserted directly.
 *
 * Angles are degrees clockwise from twelve o'clock, which is how a dial is read
 * out loud and therefore the only convention that keeps the call sites legible.
 * SVG's own angle convention (counter-clockwise from three o'clock) is converted
 * once, here, rather than in five components.
 */

/** Bottom-left of the dial. The 90° gap at the bottom is where the label sits. */
export const GAUGE_START_ANGLE = 225
export const GAUGE_SWEEP_ANGLE = 270

export interface Point {
  x: number
  y: number
}

/** Three decimals: enough for a 100-unit viewBox, short enough to diff by eye. */
function round(value: number): number {
  return Math.round(value * 1000) / 1000
}

export function polarPoint(cx: number, cy: number, radius: number, angleDeg: number): Point {
  const radians = ((angleDeg - 90) * Math.PI) / 180
  return { x: round(cx + radius * Math.cos(radians)), y: round(cy + radius * Math.sin(radians)) }
}

/**
 * An `A` command between two angles on the same circle.
 *
 * A zero-length sweep deliberately produces a path whose endpoints coincide: SVG
 * renders nothing for it, which is exactly right for a gauge reading its minimum
 * and saves every caller from a `fraction === 0` branch.
 */
export function arcPath(
  cx: number,
  cy: number,
  radius: number,
  startDeg: number,
  endDeg: number,
): string {
  const start = polarPoint(cx, cy, radius, startDeg)
  const end = polarPoint(cx, cy, radius, endDeg)
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0
  const sweep = endDeg >= startDeg ? 1 : 0
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`
}

/**
 * Where a value sits on the scale, in [0,1].
 *
 * Clamped rather than allowed to overflow: a sensor fault reporting 9000 rpm on a
 * 6000 rpm dial should peg the needle, not draw a second lap that reads as a low
 * value. The raw number is still printed beside the dial, and `isOutOfRange` in
 * the registry is what flags it as implausible.
 */
export function scaleFraction(value: number, min: number, max: number): number {
  if (!Number.isFinite(value) || !(max > min)) return 0
  return Math.min(1, Math.max(0, (value - min) / (max - min)))
}

/** Evenly spaced tick angles, inclusive of both ends. */
export function tickAngles(count: number): number[] {
  if (count < 2) return [GAUGE_START_ANGLE]
  const step = GAUGE_SWEEP_ANGLE / (count - 1)
  return Array.from({ length: count }, (_, index) => GAUGE_START_ANGLE + index * step)
}
